/**
 * FileUploadField — reusable file picker that uploads to the cargo
 * upload endpoint and returns a signed URL via `onUploaded`. Used by
 * KYC docs, supplier invoices, dispatch proofs, etc.
 *
 * The parent component owns the resulting URL state — this component
 * is stateless beyond the local "uploading" flag.
 */
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, Loader, CheckCircle2, AlertCircle, X } from 'lucide-react';
import { uploadsApi } from '@/api/uploads';

type Kind = 'image' | 'document' | 'instructions';

interface Props {
  kind: Kind;
  label?: string;
  accept?: string;
  /** Called with the signed URL once upload succeeds. */
  onUploaded: (url: string, filename: string) => void;
  /** Pre-existing URL to show (e.g. when editing an already-uploaded doc). */
  currentUrl?: string | null;
  currentFilename?: string | null;
  onClear?: () => void;
  disabled?: boolean;
  /** Extra Tailwind classes for the outer container. */
  className?: string;
}

const KIND_TO_API: Record<Kind, (f: File) => Promise<{ url: string; filename: string }>> = {
  image: uploadsApi.uploadImage,
  document: uploadsApi.uploadDocument,
  instructions: uploadsApi.uploadInstructions,
};

export function FileUploadField({
  kind, label, accept, onUploaded, currentUrl, currentFilename, onClear, disabled, className,
}: Props) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const acceptDefault =
    kind === 'image' ? 'image/jpeg,image/png,image/webp,image/gif'
    : kind === 'document' ? 'image/jpeg,image/png,image/webp,image/gif,application/pdf'
    : 'image/jpeg,image/png,image/webp,image/gif,application/pdf,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document';

  const handlePick = () => inputRef.current?.click();

  const handleFile = async (f: File) => {
    setError(null);
    setUploading(true);
    try {
      const r = await KIND_TO_API[kind](f);
      onUploaded(r.url, r.filename);
    } catch (e: any) {
      setError(e?.message || t('upload_failed', 'فشل الرفع'));
    } finally {
      setUploading(false);
      // Reset so the same file can be picked again after an error
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  // Already uploaded — show preview + clear button
  if (currentUrl) {
    return (
      <div className={`flex items-center gap-2 p-2 bg-success/5 border border-success/30 rounded-xl ${className || ''}`}>
        <CheckCircle2 size={16} className="text-success flex-shrink-0" />
        <a
          href={currentUrl}
          target="_blank"
          rel="noreferrer"
          className="flex-1 text-sm text-text-primary truncate hover:underline"
        >
          {currentFilename || label || t('file_uploaded', 'ملف مرفوع')}
        </a>
        {onClear && !disabled && (
          <button
            type="button"
            onClick={onClear}
            className="text-text-tertiary hover:text-error p-1"
            aria-label={t('remove_file', 'إزالة')}
          >
            <X size={14} />
          </button>
        )}
      </div>
    );
  }

  return (
    <div className={className}>
      <input
        ref={inputRef}
        type="file"
        accept={accept ?? acceptDefault}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) handleFile(f);
        }}
        disabled={disabled || uploading}
        className="hidden"
      />
      <button
        type="button"
        onClick={handlePick}
        disabled={disabled || uploading}
        className={`w-full flex items-center justify-center gap-2 p-3 border-2 border-dashed rounded-xl text-sm font-semibold transition ${
          disabled || uploading
            ? 'border-border bg-border/20 text-text-tertiary cursor-not-allowed'
            : 'border-border bg-surface text-text-secondary hover:border-primary hover:text-primary'
        }`}
      >
        {uploading ? (
          <>
            <Loader size={16} className="animate-spin" />
            {t('uploading', 'جارٍ الرفع...')}
          </>
        ) : (
          <>
            <Upload size={16} />
            {label ?? t('upload_file', 'رفع ملف')}
          </>
        )}
      </button>
      {error && (
        <div className="text-xs text-error mt-2 flex items-center gap-1">
          <AlertCircle size={12} /> {error}
        </div>
      )}
    </div>
  );
}
